import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import { Marker, Polyline, Polygon } from '@react-google-maps/api';
import { markerConfig } from '@constants/component-configs';
import { getLayerIcon } from '@utils/icon-utils';
import { generatePath, generatePolygonPaths, getPointShapeCoords } from '@utils/map-utils';
import { isLayerMarkerVisible } from '@utils/markers-utils';

const LayerFigure = ({ layer, name }) => {
  const layers = useSelector(state => state.layers);

  const {
    icon,
    map_style: mapStyle,
    style_condition: styleCondition
  } = layers[name];

  const markerVisible = useSelector(state => isLayerMarkerVisible(state, name, layer));

  const getStyle = useCallback(() => {
    const { attrs } = layer;
    if (styleCondition) {
      const styles = mapStyle[attrs[styleCondition]];
      if (styles) {
        return cloneDeep(styles);
      }
    }
    return cloneDeep(mapStyle);
  }, [layer, mapStyle, styleCondition]);

  const computedMapStyle = useMemo(() => {
    const style = getStyle();
    if (!style) {
      return {};
    }
    return style;
  }, [getStyle]);

  if (!markerVisible) {
    return null;
  }

  const { id, shape } = layer;
  const figureId = `${name}-${id}`;
  const layerProps = {
    options: { ...computedMapStyle, clickable: false, zIndex: 0 }
  };

  if (shape.type === 'Point') {
    const markerProps = {
      options: markerConfig.options
    };
    return (
      <Marker
        key={figureId}
        icon={getLayerIcon(icon, 0)}
        position={getPointShapeCoords(shape)}
        {...markerProps}
      />
    );
  }

  if (shape.type === 'LineString') {
    const path = generatePath(shape.coordinates);
    return [<Polyline key={figureId} path={path} {...layerProps} />];
  }

  if (shape.type === 'MultiLineString') {
    const lines = [];
    shape.coordinates.forEach((coords, index) => {
      const path = generatePath(coords);
      lines.push(<Polyline key={`${figureId}-${index}`} path={path} {...layerProps} />);
    });
    return lines;
  }

  if (shape.type === 'Polygon') {
    const polygonPath = generatePolygonPaths(shape.coordinates);
    return (
      <div>
        <Polygon key={figureId} paths={polygonPath} {...layerProps} />
        {computedMapStyle &&
          Object.prototype.hasOwnProperty.call(computedMapStyle, 'icons') &&
          <Polyline key={`outline-${figureId}`} path={generatePath(shape.coordinates[0])} {...layerProps} />
        }
      </div>
    );
  }

  if (shape.type === 'MultiPolygon') {
    return shape.coordinates.map((coords, index) => (
      <div key={`wrapper-${figureId}-${index}`}>
        <Polygon
          key={`${figureId}-${index}`}
          paths={generatePolygonPaths(coords)}
          {...layerProps}
        />
        {computedMapStyle && Object.prototype.hasOwnProperty.call(computedMapStyle, 'icons') &&
          <Polyline
            key={`outline-${figureId}-${index}`}
            path={generatePath(coords[0])}
            {...layerProps}
          />
        }
      </div>
    ));
  }

  return null;
};

LayerFigure.propTypes = {
  layer: PropTypes.object,
  name: PropTypes.string
};

export default LayerFigure;
